import { post } from './Common';

const BASE_URL = `${process.env.REACT_APP_API_URL}/v1/admin`;



// Utility function to create error responses
export const createChatError = (error: string, message?: string): ChatApiError => ({
  error,
  message,
});

export interface ChatQuestionRequest {
  question: string;
  previousResponseId?: string;
  selectedL1id?: number;
  selectedNetworkName?: string;
}

export interface ChatQuestionResponse {
  response: string;
  previousResponseId?: string;
}

export interface ChatApiError {
  error: string;
  message?: string;
}

export interface ChatApiResult<T = ChatQuestionResponse> {
  response?: T;
  error?: ChatApiError;
}

export const sendChatQuestion = async (
  question: string,
  authToken: string,
  previousResponseId?: string,
  selectedL1id?: number,
  selectedNetworkName?: string
): Promise<ChatApiResult> => {
  try {
    if (!question?.trim()) {
      return {
        error: {
          error: 'Validation Error',
          message: 'Question cannot be empty'
        }
      };
    }

    if (!authToken) {
      return {
        error: {
          error: 'Authentication Error',
          message: 'Authentication token is required'
        }
      };
    }

    const url = `${BASE_URL}/bubbles`;
    const body: any = {
      question: question.trim(),
      selectedL1id: selectedL1id || 0
    };

    // Always include previousResponseId if provided
    if (previousResponseId) {
      body.previousResponseId = previousResponseId;
    }

    // Include selected network name if provided
    if (selectedNetworkName) {
      body.selectedNetworkName = selectedNetworkName;
    }

    const response = await post<ChatQuestionResponse>(url, authToken, undefined, body);

    if (response.ok && response.parsedBody) {
      // Flexible response parsing - handle different possible field names
      if (response.parsedBody && typeof response.parsedBody === 'object') {
        const responseBody = response.parsedBody as any;
        const responseText = responseBody.response || responseBody.answer || responseBody.text;

        if (responseText && typeof responseText === 'string') {
          return {
            response: {
              response: responseText,
              previousResponseId: responseBody.responseId
            } as ChatQuestionResponse
          };
        } else {
          return {
            error: {
              error: 'Response Format Error',
              message: 'No valid response text found in server response'
            }
          };
        }
      } else {
        return {
          error: {
            error: 'Response Format Error',
            message: 'Invalid response format from server'
          }
        };
      }
    } else {
      // Handle error response
      let errorMessage = response.statusText || 'Unknown API error';
      if (response.parsedBody && typeof response.parsedBody === 'object') {
        const errorBody = response.parsedBody as any;
        errorMessage = errorBody.message || errorBody.error || errorMessage;
      }

      return {
        error: {
          error: 'API Error',
          message: `HTTP ${response.status}: ${errorMessage}`
        }
      };
    }
  } catch (error) {
    // console.error('Chat API error:', error);
    return {
      error: {
        error: 'Network Error',
        message: error instanceof Error ? error.message : 'Unknown network error occurred'
      }
    };
  }
};
